import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtErr } from "../api";
import { useI18n } from "../i18n";
import useSeoHead, { breadcrumbLd } from "../useSeoHead";
import { ArrowRight, Mail, Bell, MessageCircle, Check, MapPin } from "lucide-react";
import {
  GovListedBadge,
  PendingVerificationBadge,
  EvidenceBackedBadge,
  CorridorVerifiedBadge,
  LegacyVerifiedBadge,
  ClaimedBadge,
  UnclaimedBadge,
  DemoBadge,
} from "./adminUiHelpers";

export default function Capability() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get(`/public/capabilities/${slug}`);
        setData(data);
      } catch (e) {
        setErr(fmtErr(e.response?.data?.detail) || "Not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  /* Brief SEO V2 — title, description, Breadcrumb + Service schema */
  useSeoHead(
    data
      ? {
          title:
            lang === "es"
              ? `${data.name} en Chihuahua–El Paso · IMMEX | Corridor Digital`
              : `${data.name} in Chihuahua–El Paso · IMMEX | Corridor Digital`,
          description:
            lang === "es"
              ? `${data.count} ubicación(es) de planta IMMEX en el corredor Chihuahua ↔ El Paso ofreciendo ${data.name}. Basado en el listado oficial IMMEX y evidencia de investigación citada. RFQ directo.`
              : `${data.count} IMMEX plant location(s) in the Chihuahua ↔ El Paso corridor offering ${data.name}. Based on the official IMMEX listing and cited research evidence. Direct RFQ.`,
          path: `/c/${slug}`,
          lang,
          jsonLd: [
            {
              id: "ld-cap-breadcrumb",
              payload: breadcrumbLd([
                {
                  name: lang === "es" ? "Inicio" : "Home",
                  url: "https://corridordigital.net/",
                },
                {
                  name: lang === "es" ? "Directorio" : "Directory",
                  url: "https://corridordigital.net/directory",
                },
                { name: data.name, url: `https://corridordigital.net/c/${slug}` },
              ]),
            },
            {
              id: "ld-cap-service",
              payload: {
                "@context": "https://schema.org",
                "@type": "Service",
                serviceType: data.name,
                provider: {
                  "@type": "Organization",
                  name: "Corridor Digital",
                  url: "https://corridordigital.net/",
                },
                areaServed: {
                  "@type": "Place",
                  name: "Chihuahua–El Paso Corridor",
                  address: {
                    "@type": "PostalAddress",
                    addressCountry: ["MX", "US"],
                  },
                },
                description:
                  lang === "es"
                    ? `Directorio verificado de fabricantes IMMEX ofreciendo ${data.name} en el corredor Chihuahua ↔ El Paso.`
                    : `Verified directory of IMMEX manufacturers offering ${data.name} in the Chihuahua ↔ El Paso corridor.`,
              },
            },
          ],
        }
      : { title: "Loading…", description: "", path: `/c/${slug}`, lang }
  );

  if (loading) {
    return (
      <div className="container-cd py-20 overline" data-testid="capability-loading">
        {t("common.loading")}
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="container-cd py-20" data-testid="capability-error">
        <div className="overline">404</div>
        <h1 className="font-display text-4xl mt-3">
          {lang === "es" ? "Capacidad no encontrada" : "Capability not found"}
        </h1>
        <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>{err}</p>
        <Link to="/directory" className="btn btn-dark mt-6" data-testid="capability-error-back">
          ← {lang === "es" ? "Volver al directorio" : "Back to directory"}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="capability-page">
      {/* HERO */}
      <section className="container-cd pt-16 pb-12" style={{ borderBottom: "1px solid var(--line)" }}>
        <div className="overline">
          {lang === "es" ? "CAPACIDAD · CORREDOR" : "CAPABILITY · CORRIDOR"} · {data.count}{" "}
          {lang === "es" ? "UBICACIÓN(ES) DE PLANTA" : "PLANT LOCATION(S)"}
        </div>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9] max-w-5xl">
          {data.name}
        </h1>
        <p className="mt-6 max-w-3xl text-base md:text-lg" style={{ color: "var(--ink-2)" }}>
          {lang === "es" ? (
            <>
              Plantas IMMEX en el corredor <strong>Chihuahua ↔ El Paso</strong> que ofrecen{" "}
              <strong>{data.name}</strong>. La información se basa en el listado oficial IMMEX
              y en evidencia de investigación citada. La verificación Corridor específica de
              planta está pendiente salvo que la planta muestre el sello{" "}
              <strong>Corridor Verified</strong>.
            </>
          ) : (
            <>
              IMMEX plants on the <strong>Chihuahua ↔ El Paso</strong> corridor offering{" "}
              <strong>{data.name}</strong>. Information is based on the official IMMEX listing
              and cited research evidence. Plant-specific Corridor verification is pending
              unless the plant shows the <strong>Corridor Verified</strong> label.
            </>
          )}
        </p>
        <div className="mt-6 flex flex-wrap gap-2 text-xs font-mono">
          <Link to="/directory" className="cd-chip cd-chip-interactive" data-testid="capability-breadcrumb-dir">
            ← {lang === "es" ? "Directorio" : "Directory"}
          </Link>
          <Link to="/digest" className="cd-chip cd-chip-interactive" data-testid="capability-breadcrumb-digest">
            {lang === "es" ? "Corridor Digest" : "Corridor Digest"}
          </Link>
        </div>
      </section>

      <div className="container-cd py-10 grid md:grid-cols-12 gap-10">
        {/* Results */}
        <main className="md:col-span-8">
          <div className="overline mb-4">
            {lang === "es" ? "UBICACIONES DE PLANTA DISPONIBLES" : "AVAILABLE PLANT LOCATIONS"}
          </div>
          {data.manufacturers.length === 0 ? (
            <CapabilityEmptyState data={data} lang={lang} slug={slug} />
          ) : (
            <div className="space-y-4" data-testid="capability-results">
              {data.manufacturers.map((m) => (
                <PlantLocationRow key={m.profile_id || m.slug} m={m} lang={lang} />
              ))}
            </div>
          )}

          {/* Brief SEO V2 · #13 — SEO content block (200–300 words) */}
          <CapabilitySeoBlock data={data} lang={lang} />

          {/* CTA strip */}
          <div
            className="cd-card mt-10 p-8 flex flex-col md:flex-row md:items-center justify-between gap-6"
            style={{ background: "var(--ink)", color: "white" }}
          >
            <div>
              <div className="overline" style={{ color: "var(--orange)" }}>
                {lang === "es" ? "¿ERES FABRICANTE?" : "ARE YOU A MANUFACTURER?"}
              </div>
              <div className="font-display text-2xl md:text-3xl mt-2 leading-tight">
                {lang === "es"
                  ? `Lista tu planta para "${data.name}" en el corredor.`
                  : `List your plant for "${data.name}" on the corridor.`}
              </div>
            </div>
            <Link to="/register" className="btn btn-primary whitespace-nowrap" data-testid="capability-cta-register">
              {lang === "es" ? "Lista tu planta →" : "List your plant →"}
            </Link>
          </div>
        </main>

        {/* Sidebar */}
        <aside className="md:col-span-4 space-y-10" data-testid="capability-sidebar">
          {data.related_capabilities.length > 0 && (
            <div>
              <div className="overline">
                {lang === "es" ? "CAPACIDADES RELACIONADAS" : "RELATED CAPABILITIES"}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {data.related_capabilities.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/c/${c.slug}`}
                    className="cd-chip cd-chip-interactive font-mono text-xs"
                    data-testid={`capability-related-${c.slug}`}
                  >
                    {c.name} · {c.count}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {data.top_certifications.length > 0 && (
            <div>
              <div className="overline">
                {lang === "es" ? "CERTIFICACIONES COMUNES" : "COMMON CERTIFICATIONS"}
              </div>
              <div className="mt-4 space-y-2">
                {data.top_certifications.map((c) => (
                  <div
                    key={c.name}
                    className="flex items-center justify-between py-1.5"
                    style={{ borderBottom: "1px dashed var(--line)" }}
                  >
                    <span className="font-heading text-sm">{c.name}</span>
                    <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                      {c.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.top_industries.length > 0 && (
            <div>
              <div className="overline">
                {lang === "es" ? "INDUSTRIAS ATENDIDAS" : "INDUSTRIES SERVED"}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {data.top_industries.map((c) => (
                  <span key={c.name} className="cd-chip font-mono text-xs">
                    {c.name} · {c.count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {data.top_cities.length > 0 && (
            <div>
              <div className="overline">
                {lang === "es" ? "DÓNDE FABRICAN" : "WHERE THEY BUILD"}
              </div>
              <div className="mt-4 space-y-2">
                {data.top_cities.map((c) => (
                  <div
                    key={c.name}
                    className="flex items-center justify-between py-1.5"
                    style={{ borderBottom: "1px dashed var(--line)" }}
                  >
                    <span className="font-heading text-sm">{c.name}</span>
                    <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                      {c.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

/* One plant-location result — exact record truth. Repeated companies stay
 * separate rows: every row is ONE plant location with its own sequence,
 * address, Government-listed and Corridor Verified status. */
function PlantLocationRow({ m, lang }) {
  const name =
    m.display_name || m.company_name || m.legal_name || m.legal_entity || m.slug;
  const isImmex = (m.slug || "").toLowerCase().startsWith("immex-");
  const address = m.plant_address || m.street_address || m.address || "";
  const postal = m.postal_code || m.zip_code || "";
  const immexRef = m.immex_program_reference || m.immex_number || "";
  return (
    <div className="cd-card p-5" data-testid={`capability-row-${m.slug}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="font-mono text-xs"
              style={{
                padding: "2px 7px",
                background: "#f1f5f9",
                borderRadius: 2,
                letterSpacing: "0.04em",
                flexShrink: 0,
              }}
              data-testid={`capability-row-seq-${m.slug}`}
            >
              {m.plant_sequence_label || "—"}
            </span>
            <h3 className="font-heading font-semibold text-lg truncate" data-testid={`capability-row-name-${m.slug}`} title={name}>
              {name}
            </h3>
          </div>
          <div className="mt-2 font-mono text-xs flex items-start gap-1.5" style={{ color: "var(--ink-2)" }}>
            <MapPin size={11} strokeWidth={1.6} className="flex-shrink-0 mt-0.5" />
            <span data-testid={`capability-row-address-${m.slug}`}>
              {address || (lang === "es" ? "Dirección no enviada" : "Address not submitted")}
              {postal ? ` · ${postal}` : ""}
              {m.city ? ` · ${m.city}${m.state ? `, ${m.state}` : ""}` : ""}
            </span>
          </div>
          {m.assigned_park ? (
            <div className="mt-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              <span className="overline">PARK</span> {m.assigned_park}
            </div>
          ) : null}
          {immexRef ? (
            <div className="mt-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              <span className="overline">IMMEX</span> {immexRef}
              {m.immex_program_year ? ` · ${m.immex_program_year}` : ""}
            </div>
          ) : null}
        </div>
        <Link
          to={`/m/${m.slug}`}
          className="font-mono text-xs inline-flex items-center gap-1 flex-shrink-0"
          style={{
            color: "var(--ink)",
            border: "1px solid var(--ink)",
            padding: "7px 12px",
            borderRadius: 2,
            whiteSpace: "nowrap",
          }}
          data-testid={`capability-row-link-${m.slug}`}
        >
          {lang === "es" ? "Ver perfil" : "View profile"} <ArrowRight size={11} strokeWidth={1.8} />
        </Link>
      </div>
      <div className="mt-3 inline-flex flex-wrap gap-1.5" data-testid={`capability-row-badges-${m.slug}`}>
        {isImmex ? <GovListedBadge lang={lang} testid={`capability-row-gov-${m.slug}`} /> : null}
        {m.corridor_verified === true ? <CorridorVerifiedBadge testid={`capability-row-corridor-${m.slug}`} /> : null}
        {m.corridor_verified !== true ? <PendingVerificationBadge lang={lang} testid={`capability-row-pending-${m.slug}`} /> : null}
        {m.capability_evidence_version || (Array.isArray(m.capability_evidence) && m.capability_evidence.length > 0) ? (
          <EvidenceBackedBadge lang={lang} testid={`capability-row-evidence-${m.slug}`} />
        ) : null}
        {m.verified === true && m.corridor_verified !== true ? <LegacyVerifiedBadge testid={`capability-row-legacy-${m.slug}`} /> : null}
        {m.claimed === true ? <ClaimedBadge testid={`capability-row-claimed-${m.slug}`} /> : isImmex ? <UnclaimedBadge testid={`capability-row-unclaimed-${m.slug}`} /> : null}
        {m.is_demo === true ? <DemoBadge testid={`capability-row-demo-${m.slug}`} /> : null}
      </div>
    </div>
  );
}


/* Brief SEO V2 · #9 — Premium empty state with capability-aware copy + CTAs. */
function CapabilityEmptyState({ data, lang, slug }) {
  return (
    <div
      className="cd-card p-8 md:p-12 text-center"
      data-testid="capability-empty"
      style={{ borderColor: "var(--orange)" }}
    >
      <div className="overline" style={{ color: "var(--orange)" }}>
        <Bell size={11} className="inline mr-2 -mt-1" />
        {lang === "es" ? "CATEGORÍA EN INCUBACIÓN" : "CATEGORY INCUBATING"}
      </div>
      <h2 className="font-display text-2xl md:text-4xl mt-4 leading-tight">
        {lang === "es"
          ? `Aún no hay fabricantes de ${data.name} verificados en este momento`
          : `No verified ${data.name} manufacturers yet`}
      </h2>
      <p className="mt-4 text-sm md:text-base max-w-xl mx-auto" style={{ color: "var(--ink-2)" }}>
        {lang === "es"
          ? `Estamos verificando plantas IMMEX en el corredor Chihuahua ↔ El Paso. Sé el primer comprador en recibir aviso cuando un fabricante de ${data.name} se sume.`
          : `We're verifying IMMEX plants on the Chihuahua ↔ El Paso corridor. Be the first US buyer to know when a ${data.name} manufacturer joins.`}
      </p>
      <div className="mt-7 flex flex-col md:flex-row gap-3 justify-center">
        <Link
          to={`/directory?q=${encodeURIComponent(data.name)}`}
          className="btn btn-primary"
          data-testid="capability-empty-cta-search"
        >
          <ArrowRight size={14} />{" "}
          {lang === "es" ? "Buscar capacidad similar" : "Search a similar capability"}
        </Link>
      </div>
      <WaitlistForm slug={slug} capabilityName={data.name} lang={lang} />
      <div className="mt-6 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        <MessageCircle size={11} className="inline mr-1.5 -mt-0.5" />
        {lang === "es" ? (
          <>
            ¿Eres fabricante de <strong>{data.name}</strong>?{" "}
            <Link to="/register" style={{ color: "var(--orange)", textDecoration: "underline" }}>
              Lista tu planta →
            </Link>
          </>
        ) : (
          <>
            Are you a <strong>{data.name}</strong> manufacturer?{" "}
            <Link to="/register" style={{ color: "var(--orange)", textDecoration: "underline" }}>
              List your plant →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

/* Brief SEO V2 · #13 — Long-form SEO content block (200–300 words). */
function CapabilitySeoBlock({ data, lang }) {
  if (!data || data.manufacturers.length === 0) return null;
  const name = data.name;
  return (
    <section className="mt-12 cd-card p-8" data-testid="capability-seo-content">
      <div className="overline mb-4">
        {lang === "es"
          ? `SOBRE ${name.toUpperCase()} EN EL CORREDOR`
          : `ABOUT ${name.toUpperCase()} IN THE CORRIDOR`}
      </div>
      {lang === "es" ? (
        <div className="text-sm md:text-base leading-relaxed space-y-4" style={{ color: "var(--ink)" }}>
          <p>
            <strong>{name}</strong> es una de las capacidades de fabricación más
            buscadas en el corredor <strong>Chihuahua ↔ El Paso</strong>, el clúster
            manufacturero más denso de América del Norte. Más de 330 plantas{" "}
            <strong>IMMEX certificadas</strong> operan a menos de 30 minutos del
            puente Bridge of the Americas, lo que permite trayectos de carga
            transfronterizos en horas, no semanas — y bajo el marco{" "}
            <strong>USMCA</strong> calificado para arancel cero.
          </p>
          <p>
            Las plantas de <strong>{name}</strong> en este directorio provienen del
            listado oficial IMMEX de la Secretaría de Economía, y las capacidades se
            asignan únicamente a partir de evidencia de investigación citada — una URL
            de fuente y texto exacto de respaldo por planta. La verificación Corridor
            específica de planta está pendiente salvo que el perfil muestre el sello{" "}
            <strong>Corridor Verified</strong>. Tu RFQ se envía a través de Corridor
            Digital, sin intermediarios.
          </p>
          <p>
            Si vienes de un equipo de sourcing en Detroit, Dallas o el Medio
            Oeste, esta página es tu punto de entrada al corredor. Filtra por
            capacidad, certificación o parque industrial. Solicita cotización
            sin fricción. Sin onboarding, sin venta cruzada, sin
            "representantes".
          </p>
        </div>
      ) : (
        <div className="text-sm md:text-base leading-relaxed space-y-4" style={{ color: "var(--ink)" }}>
          <p>
            <strong>{name}</strong> is one of the most-searched manufacturing
            capabilities on the <strong>Chihuahua ↔ El Paso</strong> corridor — the
            densest manufacturing cluster in North America. Over 330 IMMEX-
            certified plants operate within a 30-minute drive of the Bridge of
            the Americas, enabling cross-border freight in hours, not weeks,
            under <strong>USMCA</strong>-qualified zero-tariff treatment.
          </p>
          <p>
            <strong>{name}</strong> plants in this directory come from the official
            IMMEX listing published by Mexico's Secretaría de Economía, and
            capabilities are assigned only from cited research evidence — a source
            URL and exact supporting text per plant. Plant-specific Corridor
            verification is pending unless a profile shows the{" "}
            <strong>Corridor Verified</strong> label. Your RFQ is submitted through
            Corridor Digital with no middlemen.
          </p>
          <p>
            If you're sourcing from Detroit, Dallas, or the US Midwest, this
            page is your entry point to the corridor. Filter by capability,
            certification, or industrial park. Submit RFQs frictionlessly. No
            onboarding, no cross-sell, no "reps."
          </p>
        </div>
      )}
    </section>
  );
}


/* Empty-state waitlist form — buyers leave their email and get an
 * immediate Resend confirmation; when a verified manufacturer for this
 * capability later joins, the backend fan-out sends a `waitlist_match`
 * email. Inline status (idle / submitting / success / error) — no global
 * Toaster mounted at app root, so feedback lives next to the form. */
function WaitlistForm({ slug, capabilityName, lang }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errMsg, setErrMsg] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus("error");
      setErrMsg(
        lang === "es"
          ? "Ingresa un correo válido."
          : "Enter a valid email.",
      );
      return;
    }
    setStatus("submitting");
    setErrMsg("");
    try {
      const { data } = await api.post("/buyer-waitlist", {
        email: trimmed,
        capability: capabilityName,
        slug,
        lang,
        source: "capability_empty_state",
      });
      if (data && data.ok) {
        setStatus("success");
      } else {
        throw new Error("unexpected response");
      }
    } catch (err) {
      setStatus("error");
      setErrMsg(
        fmtErr(err?.response?.data?.detail) ||
          (lang === "es"
            ? "No pudimos guardarlo. Intenta de nuevo."
            : "We couldn't save that. Please try again."),
      );
    }
  }

  if (status === "success") {
    return (
      <div
        className="mt-6 mx-auto max-w-xl border p-5 flex items-start gap-3"
        style={{ borderColor: "var(--orange)", background: "var(--cream)" }}
        data-testid="capability-waitlist-success"
      >
        <Check size={20} style={{ color: "var(--orange)" }} className="flex-shrink-0 mt-0.5" />
        <div className="text-left">
          <div className="font-heading font-semibold text-sm">
            {lang === "es" ? "Estás en la lista." : "You're on the list."}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>
            {lang === "es"
              ? `Te enviamos un correo de confirmación. Cuando un fabricante IMMEX de ${capabilityName} se sume, serás el primero en saberlo.`
              : `We just emailed a confirmation. When a verified ${capabilityName} IMMEX manufacturer joins, you'll be the first to know.`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-6 mx-auto max-w-xl flex flex-col sm:flex-row gap-2"
      data-testid="capability-waitlist-form"
    >
      <input
        type="email"
        required
        placeholder={
          lang === "es"
            ? "tu correo de trabajo"
            : "your work email"
        }
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (status === "error") setStatus("idle");
        }}
        disabled={status === "submitting"}
        className="flex-1 border px-3 py-2 text-sm font-mono"
        style={{ borderColor: "var(--ink)", background: "white" }}
        data-testid="capability-waitlist-email"
      />
      <button
        type="submit"
        className="btn btn-ghost whitespace-nowrap"
        disabled={status === "submitting"}
        data-testid="capability-empty-cta-waitlist"
      >
        <Mail size={14} />{" "}
        {status === "submitting"
          ? lang === "es" ? "Guardando…" : "Saving…"
          : lang === "es" ? "Avísame cuando lleguen" : "Notify me when ready"}
      </button>
      {status === "error" ? (
        <div
          className="basis-full font-mono text-xs"
          style={{ color: "var(--red)" }}
          data-testid="capability-waitlist-error"
        >
          {errMsg}
        </div>
      ) : null}
    </form>
  );
}
